import React from "react";
import {IoInformationCircleOutline} from "react-icons/io5";
import HelpMedia from "./HelpMedia";

const HelpList = () => {
  return (
    <div className="pt-3">
      <h3>List Page</h3>
      <p>
        The List page displays genetic interaction profile similarities as well as negative and positive interactions
        associated with a specific gene or subset of genes in a structured table format, along with accompanying
        statistical graphs.
      </p>

      <h5 className="mt-4">Key Features:</h5>
      <ul>
        <li>View data categorically: Profile Similarity, Negative Interactions and Positive Interactions</li>
        <li>Automated functional enrichment analysis</li>
        <li>Visualize genetic interaction clusters</li>
        <li>Interactive and data-rich tables</li>
        <li>Dynamic display of data and graphs based on user-selected parameters</li>
      </ul>

      <h5 className="mt-4">How to Use:</h5>
      <div className="mb-4">
        <h6 className="fw-semibold">1. List View - Landing Page</h6>
        <p>
          To open List View, click the "List View" button either before or after selecting genes in the Global Network.
          This page is organized into three main sections: the left, center, and right panels.
        </p>
        <div>
          Left panel includes:
          <ul>
            <li>Navigation buttons to Global Network, Subnetwork and Overlay (SAFE) views</li>
            <li>Data Type menu: Select between Profile Similarity, Negative Interactions or Positive Interactions for
              selected genes
            </li>
            <li>Gene search box: Add genes to the Selected Genes menu</li>
            <li>Selected Genes menu: Only interactions or profile similarities for the highlighted gene are shown</li>
          </ul>
        </div>
        <div>
          Center panel includes:
          <ul>
            <li>Profile Similarity and Genetic Interaction data grid</li>
            <li>Functional Enrichment Analysis results</li>
          </ul>
        </div>
        <div>
          Right panel includes:
          <ul>
            <li>Genetic Interaction statistical graphs</li>
          </ul>
        </div>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/list_switch_from_network.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/list_landing_page.png`}
          alt="List View - Landing Page"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">2. Exploring Genetic Interaction Profile Similarities</h6>
        <p>
          When "Profile Similarity" is selected from the Data Type menu, library genes are listed in the "Library Gene
          (B)" column of the table in descending order according to their genetic interaction profile similarity
          (Pearson's Correlation Coefficient, PCC) to the library gene highlighted in the Selected Genes menu and
          displayed in the "Library Gene (A)" column of the table.
        </p>
        <p>
          As a default, all genes that appear in the "Library Gene (B)" column must have a genetic interaction profile
          that exceeds a specific similarity threshold (PCC  &gt; 0.1) with the Selected Gene "Library Gene (A)".
        </p>
        <div>
          <span className="fst-italic">Data columns:</span>
          <ul>
            <li>
              <u>Library Gene (A)</u>: currently Selected Gene
            </li>
            <li>
              <u>Library Gene (B)</u>: list of library genes ordered based on their similarity to "Library Gene (A)"
              interaction profile
            </li>
            <li>
              <u>Profile Similarity (PCC)</u>: calculated Genetic profile similarity based on Pearson Correlation
              Coefficient
            </li>
            <li>
              <u>Information icon</u> <IoInformationCircleOutline/>: every row in the Profile Similarity table can be
              expanded by clicking on the icon at the far right of the table to reveal function descriptions,
              HAP1 expression level, essentiality status, single mutant fitness and variability measurements as well
              as gene-specific links to other resources.
            </li>
          </ul>
        </div>
        <div>
          <p className="mb-1">
            <span className="fst-italic d-block">Data rows:</span>
            Hovering over the query or library gene names in each row reveals icons that redirect to
            <a href="https://www.genecards.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> GeneCards</a>,
            <a href="https://depmap.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> DepMap</a>, and
            <a href="https://www.alliancegenome.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> Alliance Genome </a> databases.
            Clicking the “Overlay” icon located beneath each query gene triggers an automated
            functional analysis for the set of negative and positive genetic interactions associated with that
            query gene. By default, choosing the Overlay icon initiates
            a <a target="_blank" rel="noreferrer"
                 href="https://link.springer.com/protocol/10.1007/978-1-4939-8618-7_12">SAFE (Spatial Analysis of
            Functional Enrichment)</a> analysis, which identifies regions of the HAP1 profile similarity network
            that are statistically overrepresented for a query gene’s set of negative and positive interactions.
            As an alternative to SAFE, enrichment of query gene genetic interactions can also be assessed
            using <a target="_blank" rel="noreferrer"
                     href="https://academic.oup.com/bioinformatics/article/42/1/btaf669/8385274">RISK (Regional
            Inference of Significant Kinships)</a>.
          </p>
        </div>
        <div>
          <p className="mb-1">
            <span className="fst-italic d-block">Graphs:</span>
            Three graphs related to a specific gene pair are shown by selecting the corresponding row in the interaction
            table and, each graph can be expanded by clicking on the icon in the bottom left of the preview window:
          </p>
          <ul>
            <li>
              <u>GI Profiles (Correlation Decomposition analysis)</u>: depicts the correlation decomposition of
              genetic interaction profiles for a selected library gene pair. Correlation decomposition quantifies the
              contribution of individual genetic interactions to the magnitude and direction of the overall correlation
              measured between the genetic profiles associated with a given pair of library genes. Nodes in the plot
              represent 298 genome-wide genetic interactions screens performed with 222 unique HAP1 query mutant cell
              lines, qGI scores measured between a query gene and library gene A are plotted on the x-axis while the
              qGI score associated with an interaction between the same query gene and library B are plotted on the
              y-axis.
            </li>
            <li className="list-unstyled mt-1"><i>Legend:</i></li>
            <ul className="list-unstyled ms-3">
              <li>
                <span id="node-pcc-purple" className="node-legend rounded-circle d-inline-block me-2"></span>
                Purple nodes: indicate query genes whose interactions with both library genes are similar in sign and
                magnitude and thus, contribute strongly to the profile similarity between library genes A and B
              </li>
              <li>
                <span id="node-pcc-green" className="node-legend rounded-circle d-inline-block me-2"></span>
                Green nodes: indicate query genes that show opposing genetic interactions with library gene A and
                library gene B profiles and thus have negative correlation decomposition scores
              </li>
            </ul>
            <li>
              <u>Library Gene Clusters</u>: depicts specific gene modules derived from hierarchical clustering that
              contain library genes A and B respectively
            </li>
            <li className="list-unstyled mt-1"><i>Legend:</i></li>
            <ul className="list-unstyled ms-3">
              <li>
                <span id="node-pcc-multi" className="node-legend rounded-circle d-inline-block me-2"></span>
                Gene nodes are colored according to the bioprocess enriched region to which they localize on the
                global HAP1 profile similarity network
              </li>
              <li>
                <span id="node-pcc-gray" className="node-legend rounded-circle d-inline-block me-2"></span>
                Gray nodes represent genes that are not included on the global HAP1 profile similarity network
              </li>
            </ul>
          </ul>
        </div>
        <div>
          <span className="fst-italic">Data Grid features:</span>
          <p>Similar to the Negative and Positive Interactions table, the user can Filter and Sort.</p>
        </div>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/list_pcc_table.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/list_pcc_table.png`}
          alt="Profile Similarity Data Grid"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">3. Exploring Negative and Positive Interactions</h6>
        <p>
          The List View allows detailed exploration of negative and positive generic interactions for query and/or
          library genes of interest. When "Negative Interactions" is selected in the Data Type menu, negative genetic
          interactions involving the highlighted gene in the "Selected Genes" menu are listed in the genetic
          interaction table. Positive interactions for a given gene can be viewed by clicking on the "Positive
          Interactions" button in the Data Type menu.
        </p>
        <p>
          Only those genetic interactions that satisfy a standard confidence threshold (|qGI score| &gt; 0.3 and
          FDR &lt; 0.1) are listed in the interaction tables.
        </p>
        <div>
          <span className="fst-italic">Data columns:</span>
          <ul>
            <li>
              <u>Query Gene</u>: indicates the HAP1 cell line carrying a stable LOF mutation in the indicated query
              gene. The Query gene name is followed by unique screen identification number (GIN ID number) which
              distinguishes between independent biological replicate screens using the same query mutant cell line
              (e.g. FANCG query gene screen GIN192 vs. FANCG query gene screen GIN201)
            </li>
            <li>
              <u>Library Gene</u>: indicates the gene targeted by the TKOv3 gRNA library that shows a negative or
              positive interaction with the indicated query gene
            </li>
            <li>
              <u>qGI</u>: the qGI score compares the abundance of gRNAs derived from CRISPR-based screens in WT HAP1
              cells, which reflects library gene single mutant fitness, to the abundance of the same TKOv3 gRNAs in
              HAP1 query mutant cells carrying a stable mutation in a query gene of interest, which provides an
              estimate of double mutant fitness
            </li>
            <li>
              <u>FDR</u>: the associated False Discovery Rate (FDR) reflects the statistical confidence in the qGI score
            </li>
            <li>
              <u>Suppression</u>: a subset of extreme positive interactions where the double measured mutant fitness
              was 50% greater than the fitness of the sickest single mutant, represent potential genetic suppression
              interactions. These interactions are highlighted in orange in the positive interaction table.
            </li>
            <li>
              <u>Information icon</u> <IoInformationCircleOutline/>: every row in the Interaction table can be
              expanded by clicking on the icon at the far right of the table to reveal function descriptions,
              HAP1 expression level, essentiality status, single mutant fitness and variability measurements as well
              as gene-specific links to other resources.
            </li>
          </ul>
        </div>
        <div>
          <p className="mb-1">
            <span className="fst-italic d-block">Data rows:</span>
            Hovering over the query or library gene names in each row reveals icons that redirect to
            <a href="https://www.genecards.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> GeneCards</a>,
            <a href="https://depmap.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> DepMap</a>, and
            <a href="https://www.alliancegenome.org" target="_blank" rel="noreferrer"
               className="text-decoration-none"> Alliance Genome </a> databases.
            Clicking the “Overlay” icon located beneath each query gene triggers an automated
            functional analysis for the set of negative and positive genetic interactions associated with that
            query gene. By default, choosing the Overlay icon initiates
            a <a target="_blank" rel="noreferrer"
                 href="https://link.springer.com/protocol/10.1007/978-1-4939-8618-7_12">SAFE (Spatial Analysis of
            Functional Enrichment)</a> analysis, which identifies regions of the HAP1 profile similarity network
            that are statistically overrepresented for a query gene’s set of negative and positive interactions.
            As an alternative to SAFE, enrichment of query gene genetic interactions can also be assessed
            using <a target="_blank" rel="noreferrer"
                     href="https://academic.oup.com/bioinformatics/article/42/1/btaf669/8385274">RISK (Regional
            Inference of Significant Kinships)</a>.
          </p>
        </div>
        <div>
          <p className="mb-1">
            <span className="fst-italic d-block">Data filtering:</span>
            By clicking on the dropdown menu in the Selected Genes menu next to a gene of interest, a user can choose to
            view <u>"All Interactions"</u> for a selected gene that was screened as a query and/or library gene or only
            the subset of negative or positive interactions identified when the selected gene was screened as a query
            alone <u>"Query Gene Interactions"</u> or as library gene alone <u>"Library Gene Interactions"</u>. For the
            subset of query genes that were screened multiple times, a user also has the option to retrieve the complete
            set of negative or positive interactions identified from all biological replicate screens or only those
            interactions identified from a single screen associated with a unique identifier number (GIN ID). Only data
            corresponding to the highlighted gene in the Selected Genes menu will be shown in the table.
          </p>
        </div>
        <div>
          <p className="mb-1">
            <span className="fst-italic d-block">Graphs:</span>
            Three graphs related to a specific gene pair are shown by selecting the corresponding row in the interaction
            table and, each graph can be expanded by clicking on the icon in the bottom left of the preview window:
          </p>
          <ul>
            <li>
              <u>Query GIs (Single vs Double Mutant fitness)</u>: plots library gene single mutant fitness on the
              x-axis and double mutant fitness of the corresponding library gene in the selected query gene mutant cell
              line on the y-axis. The qGI score reflects the deviation between library gene single and double mutant
              fitness and gene pairs associated with significant negative or positive qGI scores (|qGI score| &gt; 0.3
              and FDR &lt; 0.1) are depicted as blue and yellow nodes, respectively.
            </li>
            <li>
              <u>Query GIs (Library genes alphabetized)</u>: plots all significant negative and positive qGI scores
              identified for the highlighted query gene on the y-axis and all tested library genes are plotted in
              alphabetical order on the x-axis. Gene pairs associated with significant negative or positive qGI scores
              (|qGI score| &gt; 0.3 and FDR &lt; 0.1) are depicted as blue and yellow nodes, respectively.
            </li>
            <li>
              <u>Library GIs (Query genes alphabetized)</u>: plots all significant genetic interactions measured for
              the highlighted library gene on the y-axis with all tested query genes plotted in alphabetical order
              on the x-axis. Gene pairs associated with significant negative or positive qGI scores (|qGI score| &gt;
              0.3 and FDR &lt; 0.1) are depicted as blue and yellow nodes, respectively.
            </li>
            <li className="list-unstyled mt-1"><i>Legend:</i></li>
            <ul className="list-unstyled ms-3">
              <li>
                <span id="node-negative" className="node-legend rounded-circle d-inline-block me-2"></span>
                Negative qGI
              </li>
              <li>
                <span id="node-positive" className="node-legend rounded-circle d-inline-block me-2"></span>
                Positive qGI
              </li>
              <li>
                <span id="node-suppression" className="node-legend rounded-circle d-inline-block me-2"></span>
                Putative suppressor interaction
              </li>
              <li>
                <span id="node-selected-gene"
                      className="node-legend rounded-circle border border-danger border-2 d-inline-block me-2"></span>
                Selected gene
              </li>
            </ul>
          </ul>
        </div>
        <div>
          <span className="fst-italic">Data Grid features:</span>
          <ul>
            <li>
              <u>Filter</u>: click the filter button located in the table header to filter the interaction list
              using comma-separated keywords to show only a subset of interactions involving genes of interest.
              Filtering features are applicable to Query Gene and Library Gene columns.
            </li>
            <li>
              <u>Sort</u>: click on the sort button located at the table header to sort the data accordingly.
              Applicable to all columns.
            </li>
          </ul>
        </div>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/list_neg_pos_qgi_table.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/list_landing_page.png`}
          alt="Negative and Positive Genetic Interactions Data Grid"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">4. Automated Functional Enrichment Analysis</h6>
        <p>
          The List View enables automated functional enrichment analysis for gene lists derived from profile
          similarity, negative genetic, and positive genetic interaction data. When profile similarity is selected
          from the Data Type menu, genes in the Library Gene (B) column whose similarity to a selected Library Gene (A)
          exceeds PCC &gt; 0.1 are automatically tested for enrichment using a hypergeometric test. For negative and
          positive genetic interactions, enrichment analysis is performed on the searched gene's opposing column. If
          the searched gene is a query, genes in the Library Gene column are tested; if the searched gene is a
          library, genes in the Query Gene column are tested for enrichment.
        </p>
        <div>
          <span className="fst-italic">Functional Standards:</span>
          <p className="mb-1">
            Gene Ontology Biological Process (GO BP) is used as the default annotation standard, and results are
            displayed in the table at the bottom of the center panel. Other annotation standards can be selected for
            enrichment analysis from the dropdown menu located at the upper left side of the table.
          </p>
          <ul>
            <li>GO Biological Process (GO BP) - default</li>
            <li>GO Cellular component (GO CC)</li>
            <li>GO Molecular Function (GO MF)</li>
            <li>Molecular Signatures Database (MSigDB)</li>
            <li>Molecular Signatures Database (MSigDB)</li>
            <li>Reactome Pathways</li>
          </ul>
        </div>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/list_enrichment_table.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/list_landing_page.png`}
          alt="Enrichment Analysis Data Grid"
        />
      </div>
      <div className="mb-4">
        <h6 className="fw-semibold">5. Exporting Data</h6>
        <span className="fst-italic text-decoration-underline">Genetic Interactions</span>
        <p>
          Data can be downloaded for all genes listed in the Selected Genes menu by clicking on the "Download" icon in
          the Selected Genes menu header. Downloading data for individual selected genes is also possible via the
          dropdown menu associated with each Selected gene. Data for a selected gene(s) will be downloaded as a
          Microsoft Excel (.xlsx) file and includes all three data types: Negative Interactions, Positive Interactions
          and Profile Similarity corresponding to the gene(s) of interest.
        </p>
        <span className="fst-italic text-decoration-underline">Enrichment Analysis</span>
        <p>
          Results of the automated functional enrichment analysis can be exported as a Microsoft Excel (.xlsx) file by
          clicking on the "Download" icon at the top-left corner of the Enrichment Analysis table.
        </p>
        <HelpMedia
          src={`${process.env.PUBLIC_URL}/videos/help/list_export_data.webm`}
          poster={`${process.env.PUBLIC_URL}/images/help/list_export_data.png`}
          alt="Exporting Data"
        />
      </div>

    </div>
  );
}

export default HelpList;